import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { Alert, Autocomplete, Box, Button, CircularProgress, FormControlLabel, Snackbar, Switch, TextField, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import { noSubscriptionsImportedMessage, quoteSubscriptionImportError, useCreateQuote, useGetQuoteCustomers, useImportQuoteSubscriptions, usePreviewQuotePrices } from "../../api/useQuotes";
import { useGetCustomerQuoteProducts } from "../../api/useProducts";
import QuoteLineItemGrid from "../../components/QuoteLineItemGrid/QuoteLineItemGrid";
import QuoteTotalsPanel from "../../components/QuoteTotalsPanel/QuoteTotalsPanel";
import QuoteSectionBar from "../../components/QuoteSectionBar/QuoteSectionBar";
import QuoteSectionDatesRow from "../../components/QuoteDatesModal/QuoteSectionDatesRow";
import CopyTermButton from "../../components/CopyTermDialog/CopyTermButton";
import { TermCopyPlan, applyTermCopy } from "../../components/CopyTermDialog/copyTermUtils";
import { EditorSection, EditorSectionDates, bumpStaleStartDates, flattenLines, moveSection, newEditorSection, sectionDisplayName, sectionsPriceSignature, staleStartDates, toPreviewSectionRequest } from "../../components/QuoteSectionBar/sectionUtils";
import QuoteDiscountSummary from "../../components/QuoteDiscountSummary/QuoteDiscountSummary";
import AffiliateNameField from "../../components/AffiliateNameField/AffiliateNameField";
import { APP_ROUTES } from "../../constants/appRoutes";
import { IOrganization, IQuoteLineItem, QuoteSegment } from "../../shared/types";
import { resolveDynamicUrl } from "../../shared/utils";
import { appendImportedQuoteLines, buildCreateQuoteRequest, defaultQuoteName, findActiveOrganizationByCustomerGuid, lineIsComplete, newQuoteLine, quoteSegments, resolveSelectedCustomer } from "./createQuoteUtils";
import "./CreateQuotePage.scss";
import { quoteListErrorMessage } from "../QuoteListPage/quoteListUtils";
import { clearQuoteDraft, newDraftId, readQuoteDraft, writeQuoteDraft } from "../../shared/quoteDraft";
import { applyQuoteDraft, DraftableEditorState } from "../../shared/applyQuoteDraft";
import { quoteSaveErrorMessage, QuoteSaveError } from "../../shared/quoteSaveError";
import QuoteSaveErrorDialog from "../../components/QuoteSaveErrorDialog/QuoteSaveErrorDialog";
import DraftRestoredDialog from "../../components/DraftRestoredDialog/DraftRestoredDialog";
import { ApiRoutes } from "../../constants/apiRoutes";
import DebouncedTextField from "../../shared/DebouncedTextField";

type Props = { quoteType: "customer" | "prospect" };

const emptyDraftableState = (): DraftableEditorState => ({
  prospectName: "", name: "", description: "", internalNote: "", hasAffiliate: false, affiliateName: "", customerGuid: null,
  discounts: { monthly: "0", installment: "0", annual: "0" }, sections: [newEditorSection()],
});

const CreateQuotePage = ({ quoteType }: Props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organizations = useGetQuoteCustomers();
  const createQuote = useCreateQuote();
  const importSubscriptions = useImportQuoteSubscriptions();
  const previewPrices = usePreviewQuotePrices();
  // Restoring a draft always rebuilds from this page's own normal empty-new-quote defaults and
  // overlays only what the stored draft still validates (applyQuoteDraft) - a data-model change
  // since the draft was written degrades to a partial restore with a warning, never a hard failure.
  // The lazy useState initializer runs exactly once, on mount.
  const [restoreResult] = useState(() => {
    const draftId = searchParams.get("draftId") || newDraftId();
    if (searchParams.get("restoreDraft") !== "1") return { draftId, state: emptyDraftableState(), warnings: [] as string[], stale: [] as ReturnType<typeof staleStartDates> };
    const { state, warnings } = applyQuoteDraft(emptyDraftableState(), readQuoteDraft(quoteType, draftId));
    return { draftId, state, warnings, stale: staleStartDates(state.sections) };
  });
  const draftId = restoreResult.draftId;
  // While true, the repricing preview, the draft autosave, and the auto-default-name effect all
  // stand down - a restore sets several fields together (plus, if accepted, a stale-date bump) and
  // should settle once, not thrash through intermediate states. Cleared when DraftRestoredDialog
  // resolves, or immediately if there was nothing to report.
  const [restoring, setRestoring] = useState(restoreResult.warnings.length > 0 || restoreResult.stale.length > 0);
  const [selectedCustomer, setSelectedCustomer] = useState<IOrganization | null>(null);
  // Which customer is selected is local state, but the selected customer's own FIELDS are always
  // re-read from the latest fetched list rather than from that stored snapshot. The snapshot is
  // taken once (see the preselection effect below, which deliberately runs only once so it can't
  // stomp a manual choice) and react-query serves a warm cache synchronously on mount, so
  // `isFetched` is already true with the PREVIOUS list - meaning the snapshot is routinely the
  // stale one and the later refetch never reached the UI. That is the real reason changing an
  // organization's discounts and then clicking Create Quote still showed the old percentages:
  // the refetch worked and returned the new values, but this page had already frozen the old
  // object, and every discount read below (QuoteDiscountSummary, the pricing payload) came from
  // it. Deriving on render fixes it for good regardless of which cache key is invalidated where.
  const customer = useMemo(
    () => resolveSelectedCustomer(organizations.data || [], selectedCustomer),
    [organizations.data, selectedCustomer]);
  const customerPreselectionAttempted = useRef(false);
  const lastAutomaticPriceRequest = useRef("");
  const quoteNameEdited = useRef(Boolean(restoreResult.state.name));
  const [prospectName, setProspectName] = useState(restoreResult.state.prospectName);
  const [name, setName] = useState(restoreResult.state.name);
  const [description, setDescription] = useState(restoreResult.state.description);
  const [hasAffiliate, setHasAffiliate] = useState(restoreResult.state.hasAffiliate);
  const [affiliateName, setAffiliateName] = useState(restoreResult.state.affiliateName);
  const [discounts, setDiscounts] = useState(restoreResult.state.discounts);
  const [sections, setSections] = useState<EditorSection[]>(restoreResult.state.sections);
  const [activeIndex, setActiveIndex] = useState(0);
  const customerProducts = useGetCustomerQuoteProducts(customer?.customer_guid);
  const [importError, setImportError] = useState<string | null>(null);
  const [importInfo, setImportInfo] = useState<string | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<QuoteSaveError | null>(null);
  const customerLoadError = organizations.isError
    ? quoteListErrorMessage(organizations.error, process.env.REACT_APP_ADMIN_API_BASE)
    : "";

  const safeIndex = Math.min(activeIndex, sections.length - 1);
  const activeItems = sections[safeIndex]?.items || [];
  const allItems = useMemo(() => flattenLines(sections), [sections]);

  const setActiveItems = (next: IQuoteLineItem[] | ((current: IQuoteLineItem[]) => IQuoteLineItem[])) =>
    setSections(current => current.map((section, index) => index === safeIndex
      ? { ...section, items: typeof next === "function" ? next(section.items) : next } : section));

  const addSection = () => {
    setSections(current => [...current, newEditorSection()]);
    setActiveIndex(sections.length);
  };
  const removeSection = (index: number) => setSections(current => {
    if (current.length === 1) return current;
    const next = current.filter((_, i) => i !== index);
    setActiveIndex(value => Math.max(0, Math.min(value, next.length - 1)));
    return next;
  });
  // Issue 52: applied to the latest sections, so a reprice landing while the dialog was open isn't lost.
  const copyTerm = (plan: TermCopyPlan) => {
    const result = applyTermCopy(sections, plan);
    setSections(current => applyTermCopy(current, plan).sections);
    setActiveIndex(result.activeIndex);
    setImportInfo(result.message);
  };

  useEffect(() => {
    if (restoring || quoteNameEdited.current) return;
    setName(defaultQuoteName(prospectName || customer?.display_name || ""));
  }, [customer?.display_name, prospectName, restoring]);

  useEffect(() => {
    if (quoteType !== "customer" || customerPreselectionAttempted.current || !organizations.isFetched) return;
    customerPreselectionAttempted.current = true;
    setSelectedCustomer(findActiveOrganizationByCustomerGuid(
      organizations.data || [],
      restoreResult.state.customerGuid || searchParams.get("customerGuid")
    ));
  }, [organizations.data, organizations.isFetched, quoteType, searchParams, restoreResult]);

  // Debounced: this JSON.stringify's + localStorage.setItem's the entire draft (every section's
  // every line item, which now carries a lot more fields per line than when this was written -
  // promo, proration, etc.) - completely unthrottled, that ran synchronously on every single
  // keystroke into name/description/affiliate name, and got slower as a quote grew, producing
  // exactly the "typing is glacially slow" symptom reported once quotes had a realistic number
  // of line items. 300ms matches every other commit-on-pause debounce already used in this app
  // (e.g. the section name field, QuoteLineItemGrid's promo-check debounce). Suspended while a
  // restore is still settling so it doesn't immediately re-persist a not-yet-finalized restore.
  useEffect(() => {
    if (restoring) return;
    const timer = window.setTimeout(() => {
      const meaningful = Boolean(customer || prospectName.trim() || name.trim() || description.trim() || allItems.length);
      if (!meaningful) return;
      writeQuoteDraft({ draftId, quoteId: null, quoteType, customerGuid: customer?.customer_guid || null, prospectName, name, description, hasAffiliate, affiliateName, discounts,
        sections: sections.map(section => ({ name: section.name, dates: section.dates, items: section.items })) });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [customer, description, discounts, allItems.length, name, prospectName, quoteType, sections, hasAffiliate, affiliateName, restoring, draftId]);

  const prospect = quoteType === "prospect";
  const discountsValid = Object.values(discounts).every(value => value !== "" && Number(value) >= 0 && Number(value) <= 100);
  const targetValid = prospect ? Boolean(prospectName.trim() && discountsValid) : Boolean(customer);
  // Issue 33 (generalized): a live preview is ready under the same target condition Save already
  // requires - a prospect needs valid discounts, a customer quote needs a selected customer.
  const canPreview = prospect ? discountsValid : Boolean(customer);
  const everySectionHasLines = sections.every(section => section.items.length > 0);
  const canSave = targetValid && sections.length > 0 && everySectionHasLines && allItems.length > 0
    && allItems.every(lineIsComplete) && name.length <= 300 && description.length <= 4000 && affiliateName.length <= 200;
  const hasIncompleteManualPrice = allItems.some(item => item.is_price_unavailable || (item.is_price_manual &&
    (item.manual_msrp_unit_price == null || item.manual_cost_unit_price == null)));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    setSaveError(null);
    createQuote.mutate(buildCreateQuoteRequest({
      quoteType, customerId: customer ? String(customer.id) : "", prospectName,
      discounts, name, description, hasAffiliate, affiliateName, sections,
    }), {
      // Opens the saved quote itself, not the list - landing on the list made staff think the quote
      // was lost and forced a click back in to view/download it. `replace` keeps Back from returning
      // to this now-empty create form.
      onSuccess: created => { clearQuoteDraft(quoteType, draftId); navigate(resolveDynamicUrl(APP_ROUTES.QUOTE_DETAIL, { quoteId: String(created.quote_id) }), { replace: true }); },
      onError: error => setSaveError(quoteSaveErrorMessage(error, "create", ApiRoutes.quotes)),
    });
  };

  const resolveDraftRestore = (updateDates: boolean) => {
    if (updateDates) setSections(current => bumpStaleStartDates(current));
    setRestoring(false);
  };

  const importCustomerSubscriptions = () => {
    if (!customer) return;
    setImportError(null);
    setImportInfo(null);
    importSubscriptions.mutate(customer.customer_guid, {
      onSuccess: imported => {
        setActiveItems(current => appendImportedQuoteLines(current, imported));
        setImportInfo(noSubscriptionsImportedMessage(imported));
      },
      onError: error => setImportError(quoteSubscriptionImportError(error)),
    });
  };

  const refreshPrices = () => {
    if (!canPreview || allItems.length === 0 || !allItems.every(lineIsComplete)) return;
    setPricingError(null);
    previewPrices.mutate({
      quote_type: quoteType,
      customer_id: prospect ? null : (customer ? customer.id : null),
      prospect_monthly_discount: prospect ? Number(discounts.monthly) : null,
      prospect_installment_discount: prospect ? Number(discounts.installment) : null,
      prospect_annual_discount: prospect ? Number(discounts.annual) : null,
      sections: sections.map(toPreviewSectionRequest),
    }, {
      // Issue 50 (50.2): every line - manual included - is now in the request/response, so the
      // pairing is positional one-for-one across the whole quote, not just the automatic lines.
      onSuccess: priced => setSections(current => {
        let pricedIndex = 0;
        return current.map(section => ({ ...section, items: section.items.map(item => priced[pricedIndex++] || item) }));
      }),
      onError: error => {
        const responseData = axios.isAxiosError(error) ? error.response?.data : null;
        const detail = typeof responseData === "string" ? responseData : responseData
          ? JSON.stringify(responseData, null, 2) : error instanceof Error ? error.message : String(error);
        setPricingError(`Refresh prices failed at ${new Date().toISOString()}\n${detail}`);
      },
    });
  };

  // Issue 33 (generalized): a date change (section default or a single line's own override) must
  // trigger this the same way a product/quantity change already does, for a prospect or a
  // customer quote alike - see sectionsPriceSignature.
  const automaticPriceSignature = canPreview && allItems.length > 0 && allItems.every(lineIsComplete)
    ? JSON.stringify({ discounts: prospect ? discounts : null, sections: sectionsPriceSignature(sections) })
    : "";

  useEffect(() => {
    if (restoring || !automaticPriceSignature || automaticPriceSignature === lastAutomaticPriceRequest.current) return;
    const timer = window.setTimeout(() => {
      lastAutomaticPriceRequest.current = automaticPriceSignature;
      refreshPrices();
    }, 300);
    return () => window.clearTimeout(timer);
    // Pricing output does not participate in the signature, preventing response loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automaticPriceSignature, restoring]);

  const customerProductsEmpty = Boolean(customer && customerProducts.data && customerProducts.data.products.length === 0);
  const customerProductsError = customerProducts.isError || customerProductsEmpty;
  const customerProductsErrorMessage = customerProductsEmpty
    ? `${customer?.display_name || "This customer"} has no eligible current NCE products. Confirm the customer is active in Partner Center and has valid qualifications.`
    : customerProducts.isError
      ? (() => {
        const data = axios.isAxiosError(customerProducts.error) ? customerProducts.error.response?.data : null;
        const detail = typeof data === "string" ? data : data ? JSON.stringify(data) : "";
        return detail || `Customer products could not be loaded for ${customer?.display_name || "this customer"}. Confirm the customer is active in Partner Center and try again.`;
      })()
      : undefined;

  return <Box className="create-quote-page">
    <Box component="form" className="create-quote-form" onSubmit={submit}>
      {prospect ? <>
        <DebouncedTextField required label="Prospect name" value={prospectName} onChange={setProspectName} resetKey={quoteType}
          inputProps={{ maxLength: 200 }} helperText={draft => `${draft.length}/200`} />
        <Box className="create-quote-discounts">
          {(["monthly", "installment", "annual"] as const).map((key, index) => <TextField key={key} required type="number" label={`${["P1M", "P1YM", "P1YA"][index]} discount %`} value={discounts[key]} inputProps={{ min: 0, max: 100, step: 0.01 }} onChange={event => {
            setDiscounts(current => ({ ...current, [key]: event.target.value }));
            // Only automatic lines follow the quote-level term Discount; a manual line keeps its
            // own per-line Discount (Issue 36). Clear automatic prices across every section.
            setSections(current => current.map(section => ({ ...section, items: section.items.map(item => item.is_price_manual ? item : ({
              ...item, msrp_unit_price: null, sale_unit_price: null, total_price: null, cost_unit_price: null, margin: null,
              is_price_unavailable: false, price_unavailable_reason: null })) })));
          }} />)}
        </Box>
      </> : <Autocomplete
        forcePopupIcon={false}
        options={(organizations.data || []).filter(item => Boolean(item.is_active))}
        value={customer}
        loading={organizations.isLoading}
        getOptionLabel={option => option.display_name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value) => {
          setSelectedCustomer(value);
          setSections([newEditorSection()]);
          setActiveIndex(0);
          setImportError(null);
        }}
        renderInput={params => <TextField {...params} required label="Customer" error={organizations.isError} helperText={organizations.isError ? `Customers could not be loaded. ${customerLoadError}` : ""} />}
      />}
      {!prospect && customer && <QuoteDiscountSummary
        monthly={customer.monthly_discount} installment={customer.installment_discount} annual={customer.annual_discount} />}
      <TextField label="Quote Name (Included on Quote)" value={name} inputProps={{ maxLength: 300 }} helperText={`${name.length}/300`} onChange={event => {
        quoteNameEdited.current = true;
        setName(event.target.value);
      }} />
      <DebouncedTextField multiline minRows={3} label="Description (Included on Quote)" value={description} onChange={setDescription} resetKey={quoteType}
        inputProps={{ maxLength: 4000 }} helperText={draft => `${draft.length}/4000`} />
      <Box className="create-quote-affiliate">
        <Tooltip enterDelay={1800} title="Enable the internal Affiliate Commission Report (PDF and CSV) for this quote. Not shown to the customer.">
          <FormControlLabel control={<Switch checked={hasAffiliate} onChange={event => setHasAffiliate(event.target.checked)} />} label="Has Affiliate" />
        </Tooltip>
        {hasAffiliate && <AffiliateNameField value={affiliateName} onChange={setAffiliateName} />}
      </Box>
      {importError && <Alert severity="error" action={<Button title="Copy the import error details." color="inherit" size="small" startIcon={<ContentCopyIcon />}
        onClick={() => navigator.clipboard.writeText(importError)}>Copy Error</Button>}>
        <Typography component="pre" className="create-quote-import-error">{importError}</Typography>
      </Alert>}
      <Snackbar open={importInfo !== null} autoHideDuration={6000} onClose={() => setImportInfo(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="info" variant="filled" onClose={() => setImportInfo(null)}>{importInfo}</Alert>
      </Snackbar>
      {pricingError && <Alert severity="error" action={<Button title="Copy the pricing error details." color="inherit" size="small" startIcon={<ContentCopyIcon />}
        onClick={() => navigator.clipboard.writeText(pricingError)}>Copy Error</Button>}>
        <Typography component="pre" className="create-quote-import-error">{pricingError}</Typography>
      </Alert>}
      {sections.length > 1 && <QuoteSectionBar sections={sections} activeIndex={safeIndex} editable
        onActiveChange={setActiveIndex}
        onNameChange={(index, value) => setSections(current => current.map((section, i) => i === index ? { ...section, name: value } : section))}
        onAdd={addSection} onMove={(from, to) => setSections(current => moveSection(current, from, to))} onRemove={removeSection} />}
      {sections[safeIndex] && <QuoteSectionDatesRow section={sections[safeIndex]}
        sectionLabel={sectionDisplayName(sections[safeIndex].name, safeIndex)}
        editable quoteType={quoteType} customerGuid={prospect ? null : (customer?.customer_guid ?? null)}
        onChange={(dates: EditorSectionDates) => setSections(current => current.map((section, index) =>
          index === safeIndex ? { ...section, dates } : section))} />}
      <QuoteLineItemGrid items={activeItems} onChange={setActiveItems} quoteType={quoteType}
        customerGuid={prospect ? null : (customer?.customer_guid ?? null)}
        sectionDates={sections[safeIndex]?.dates}
        toolbarContent={<Box className="create-quote-line-actions">
          {!prospect && <Tooltip enterDelay={1800} title="Add the customer's current Partner Center subscriptions as line items."><span><Button variant="outlined" startIcon={importSubscriptions.isLoading ? <CircularProgress size={18} /> : <DownloadIcon />}
            disabled={!customer || importSubscriptions.isLoading} onClick={importCustomerSubscriptions}>
            {importSubscriptions.isLoading ? "Importing subscriptions..." : "Import"}
          </Button></span></Tooltip>}
          {sections.length === 1 && <Tooltip enterDelay={1800} title="Add a second option to compare in this quote."><Button variant="outlined" onClick={addSection}>+ Section</Button></Tooltip>}
          <CopyTermButton sections={sections} activeIndex={safeIndex} onCopy={copyTerm}
            productRows={prospect ? undefined : (customerProducts.data?.products || [])}
            productsLoading={!prospect && !customerProducts.data && !customerProducts.isError} productsError={!prospect && customerProducts.isError} />
          {canPreview && previewPrices.isLoading && <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} /><Typography variant="body2">Calculating current prices...</Typography>
          </Box>}
          {canPreview && previewPrices.isError && <Tooltip enterDelay={1800} title="Try calculating current prices again."><Button onClick={() => {
            lastAutomaticPriceRequest.current = "";
            refreshPrices();
          }}>Retry Pricing</Button></Tooltip>}
        </Box>}
        discounts={prospect ? { P1M: Number(discounts.monthly), P1YM: Number(discounts.installment), P1YA: Number(discounts.annual) }
          : { P1M: customer?.monthly_discount ?? 0, P1YM: customer?.installment_discount ?? 0, P1YA: customer?.annual_discount ?? 0 }}
        onAdd={() => setActiveItems(current => [...current, newQuoteLine((current[0]?.segment || "Commercial") as QuoteSegment)])}
        allowedCustomerSegments={prospect ? quoteSegments : (customerProducts.data?.segments || [])}
        productRows={prospect ? undefined : (customerProducts.data?.products || [])}
        productsLoading={!prospect && customerProducts.isLoading} productsError={!prospect && customerProductsError}
        productsErrorMessage={!prospect ? customerProductsErrorMessage : undefined} />
      {/* Live totals (2026-10-05): "pending" while an edit waits on or is being repriced by PreviewPrices. */}
      <QuoteTotalsPanel items={activeItems} pricingFailed={canPreview && previewPrices.isError}
        pending={restoring || (canPreview && (previewPrices.isLoading || (automaticPriceSignature !== "" && automaticPriceSignature !== lastAutomaticPriceRequest.current)))} />
      {!!allItems.length && (!everySectionHasLines || !allItems.every(lineIsComplete)) && <Typography className="create-quote-validation" color="error">
        {!everySectionHasLines ? "Every section must have at least one line item."
          : hasIncompleteManualPrice ? "Enter MSRP and Cost for every custom or manually priced line item."
          : "Complete the product, segment, term, and quantity for every line item."}
      </Typography>}
      <QuoteSaveErrorDialog error={saveError} onClose={() => setSaveError(null)} />
      <DraftRestoredDialog open={restoring} warnings={restoreResult.warnings} staleDateCount={restoreResult.stale.length}
        onUpdateDates={() => resolveDraftRestore(true)} onLeaveDates={() => resolveDraftRestore(false)} />
      <Box className="create-quote-actions">
        <Tooltip enterDelay={1800} title="Discard this new quote."><Button color="inherit" variant="outlined" onClick={() => { clearQuoteDraft(quoteType, draftId); navigate(APP_ROUTES.QUOTES); }}>Cancel</Button></Tooltip>
        <Tooltip enterDelay={1800} title="Save and authoritatively price this quote."><span><Button type="submit" variant="contained" disabled={!canSave || createQuote.isLoading}>{createQuote.isLoading ? "Saving…" : "Save"}</Button></span></Tooltip>
      </Box>
    </Box>
  </Box>;
};

export default CreateQuotePage;
